import { Injectable } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AuthService, AuthType, AUTH_TYPE_HEADER } from "src/app/shared/services/auth.service";

@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  // Auth types whose requests carry a backend-issued AccessToken that can expire
  // independently of a full re-login. On a 401 for one of these we refresh the
  // token, re-fetch the addon and retry the request once (bug 34400).
  private static readonly REFRESHABLE_AUTH_TYPES: AuthType[] = [
    AuthType.ESP,
    AuthType.SimpleStrata,
    AuthType.Stemexe,
    AuthType.OppPro,
  ];

  constructor(
    private authService: AuthService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<any> {
    const authType = this.authService.getAuthType(request);
    return this.buildAndSend(request, next, authType, false);
  }

  // Resolves the auth addon, appends its access token and sends the request.
  // `isRetry` guards against an infinite refresh loop.
  private buildAndSend(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authType: AuthType,
    isRetry: boolean
  ): Observable<HttpEvent<any>> {
    return this.authService.getAuthAddonByType(authType).pipe(
      switchMap((addon) => {
        let changedReq: any = request;
        let signInToken = !!localStorage.getItem("SignInByIdenediToken") ? localStorage.getItem("SignInByIdenediToken") : null;
        if (addon && (addon.AccessToken || signInToken)) {
          let accessToken: string = addon.AccessToken ? addon.AccessToken : localStorage.getItem("SignInByIdenediToken");
          if (addon.Type == AuthType.Technadopt) {
            accessToken = addon.technadoptAuthData ? addon.technadoptAuthData.accessToken : accessToken
          }
          changedReq = this.appendAccessToken(request, accessToken);
        }
        if (changedReq.headers.has(AUTH_TYPE_HEADER)) {
          changedReq = changedReq.clone({
            headers: changedReq.headers.delete(AUTH_TYPE_HEADER),
          });
        }
        return next.handle(changedReq).pipe(
          catchError((error: HttpErrorResponse) =>
            this.handleAuthError(error, request, next, authType, isRetry)
          )
        );
      })
    );
  }

  // On a 401 for a refreshable auth type, refresh the Idenedi token (shared lock
  // prevents concurrent refreshes), drop the cached addons so they are re-fetched
  // with a fresh AccessToken, then retry the original request exactly once.
  private handleAuthError(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authType: AuthType,
    isRetry: boolean
  ): Observable<HttpEvent<any>> {
    const canRetry =
      error.status === 401 &&
      !isRetry &&
      this.authService.isLoggedIn() &&
      HttpAuthInterceptor.REFRESHABLE_AUTH_TYPES.includes(authType);

    if (!canRetry) {
      return throwError(() => error);
    }

    let refresh$: Observable<any>;
    try {
      refresh$ = this.authService.tryRefreshIdenediToken();
    } catch {
      // No refresh token available — surface the original error.
      return throwError(() => error);
    }

    return refresh$.pipe(
      switchMap(() => {
        this.authService.invalidateAddonsCache();
        return this.buildAndSend(request, next, authType, true);
      }),
      catchError(() => throwError(() => error))
    );
  }

  private appendAccessToken(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    return request.clone({
      headers: request.headers
        .append("Authorization", "Bearer " + token)
        .delete(AUTH_TYPE_HEADER),
    });
  }
}
